import { useSystem } from '../contextProviders/SystemProvider';

/**
 *
 * @param {string[]} primaryStudyInstanceUIDs
 * @param {object[]} studyDisplayList
 * @param {string} studyDisplayList.studyInstanceUid
 * @param {string} studyDisplayList.date
 * @param {string} studyDisplayList.description
 * @param {string} studyDisplayList.modalities
 * @param {number} studyDisplayList.numInstances
 * @param {object[]} displaySets
 * @param {number} recentTimeframe - The number of milliseconds to consider a study recent
 * @returns tabs - The prop object expected by the StudyBrowser component
 */

export function createStudyBrowserTabs(
  primaryStudyInstanceUIDs,
  studyDisplayList,
  displaySets,
  recentTimeframeMS = 31536000000
) {
  const { servicesManager } = useSystem();
  const { displaySetService } = servicesManager.services;

  const shouldSortBySeriesUID = process.env.TEST_ENV === 'true';
  const primaryStudies = [];
  const allStudies = [];

  studyDisplayList.forEach(study => {
    const displaySetsForStudy = displaySets.filter(
      ds => ds.StudyInstanceUID === study.studyInstanceUid
    );

    // sort them by seriesNumber or seriesInstanceUID
    let sortedDisplaySets;
    if (shouldSortBySeriesUID) {
      sortedDisplaySets = displaySetsForStudy.sort((a, b) => {
        const displaySetA = displaySetService.getDisplaySetByUID(a.displaySetInstanceUID);
        const displaySetB = displaySetService.getDisplaySetByUID(b.displaySetInstanceUID);

        return displaySetA.SeriesInstanceUID.localeCompare(displaySetB.SeriesInstanceUID);
      });
    } else {
      // Sort by series number in ascending order
      sortedDisplaySets = displaySetsForStudy.sort((a, b) => {
        const seriesNumberA = parseInt(a.seriesNumber) || 0;
        const seriesNumberB = parseInt(b.seriesNumber) || 0;
        return seriesNumberA - seriesNumberB;
      });
    }

    const tabStudy = Object.assign({}, study, {
      displaySets: sortedDisplaySets,
    });

    if (primaryStudyInstanceUIDs.includes(study.studyInstanceUid)) {
      primaryStudies.push(tabStudy);
    }
    allStudies.push(tabStudy);
  });

  const primaryStudiesTimestamps = primaryStudies
    .filter(study => study.date)
    .map(study => new Date(study.date).getTime());

  const recentStudies =
    primaryStudiesTimestamps.length > 0
      ? allStudies.filter(study => {
          const oldestPrimaryTimeStamp = Math.min(...primaryStudiesTimestamps);

          if (!study.date) {
            return false;
          }
          const studyTimeStamp = new Date(study.date).getTime();
          return oldestPrimaryTimeStamp - studyTimeStamp < recentTimeframeMS;
        })
      : [];

  // Normalizes a DICOM TM value (HHMMSS.FFF) to a fixed-width digit string so it
  // compares correctly. The '.' separator sorts below digits, so mixing values
  // that carry fractional seconds with ones that don't would otherwise misorder.
  const _timeKey = t =>
    String(t || '')
      .replace(/\D/g, '')
      .padEnd(10, '0')
      .slice(0, 10);

  // Newest first. Sorts on the raw DICOM StudyDate/StudyTime (YYYYMMDD +
  // HHMMSS.FFF), which compares correctly as a plain string. The formatted
  // `date` field is DD-MMM-YYYY and carries no time, so studies acquired on the
  // same day all compared equal and fell back to arbitrary QIDO order.
  // Falls back to the formatted date for data sources that don't supply the raw
  // values.
  const _byDate = (studyA, studyB) => {
    if (studyA.studyDate && studyB.studyDate) {
      const keyA = `${studyA.studyDate}${_timeKey(studyA.studyTime)}`;
      const keyB = `${studyB.studyDate}${_timeKey(studyB.studyTime)}`;

      return keyB.localeCompare(keyA);
    }

    return Date.parse(studyB.date) - Date.parse(studyA.date);
  };

  const tabs = [
    {
      name: 'primary',
      label: 'Primary',
      studies: primaryStudies.sort(_byDate),
    },
    {
      name: 'recent',
      label: 'Recent',
      studies: recentStudies.sort(_byDate),
    },
    {
      name: 'all',
      label: 'All',
      studies: allStudies.sort(_byDate),
    },
  ];

  return tabs;
}
